/**
 * Partner Registration Page - Module Inscription Ticket 14
 * Route: /partner/register
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Building2, AlertCircle, CheckCircle, Upload, Loader2, Mail, Phone, User, FileText } from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

export default function PartnerRegisterPage({ embedded = false }) {
  const [step, setStep] = useState(1); // 1=form, 2=docs, 3=done
  const [formData, setFormData] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
  });
  const [partnerId, setPartnerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [docs, setDocs] = useState({ kbis: false, rc_pro: false });
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/partner/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur lors de l'inscription");
      setPartnerId(data.partner_id);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDoc = async (docType, file) => {
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('partner_id', partnerId);
      formData.append('document_type', docType);
      formData.append('file', file);
      const res = await fetch(`${API}/auth/partner/register/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Erreur');
      }
      setDocs(d => ({ ...d, [docType]: true }));
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(null);
    }
  };

  const handleFinish = () => setStep(3);

  return (
    <div className={embedded ? "flex items-center justify-center px-4 py-8" : "min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4 py-8"}>
      <div className="w-full max-w-md">
        {!embedded && (
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#C9A24D] hover:text-[#d4af5a] transition-colors mb-6" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#C9A24D]" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Devenir Partenaire</h1>
          </div>
          <p className="text-[#A0A0A0] text-sm">Rejoignez le réseau Y GO VTC Premium</p>
        </div>
        )}

        {step === 1 && (
          <Card className="bg-[#141414] border-[#2A2A2A]">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg">Inscription Flotte</CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-register-form">
                <div>
                  <Label className="text-[#E5E5E5] text-xs">Nom de la société</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input name="company_name" value={formData.company_name} onChange={handleChange} required placeholder="Ma Société VTC" className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10" data-testid="company-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5] text-xs">Nom du contact</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input name="contact_name" value={formData.contact_name} onChange={handleChange} required placeholder="Jean Dupont" className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10" data-testid="contact-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5] text-xs">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="contact@societe.fr" className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10" data-testid="email-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#E5E5E5] text-xs">Téléphone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} required placeholder="+33 6 12 34 56 78" className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10" data-testid="phone-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="partner-register-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continuer"}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <p className="text-[#666] text-sm">Déjà partenaire ? <Link to="/login" className="text-[#C9A24D] hover:text-[#d4af5a] font-medium">Se connecter</Link></p>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="bg-[#141414] border-[#2A2A2A]">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg">Documents requis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { type: 'kbis', label: 'Kbis' },
                { type: 'rc_pro', label: 'RC Pro (Responsabilité Civile Professionnelle)' },
              ].map(({ type, label }) => (
                <div key={type} className={`flex items-center gap-3 p-3 rounded-lg border ${docs[type] ? 'bg-green-500/5 border-green-500/20' : 'bg-[#1A1A1A] border-[#333]'}`} data-testid={`partner-doc-${type}`}>
                  <div className={`p-2 rounded-lg ${docs[type] ? 'bg-green-500/10' : 'bg-[#C9A24D]/10'}`}>
                    {docs[type] ? <CheckCircle className="w-4 h-4 text-green-400" /> : <FileText className="w-4 h-4 text-[#C9A24D]" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${docs[type] ? 'text-green-400' : 'text-white'}`}>{label}</p>
                    {docs[type] && <p className="text-xs text-green-400/60">Téléchargé</p>}
                  </div>
                  {!docs[type] && (
                    <label className="cursor-pointer">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={uploading === type}
                        onChange={(e) => { if (e.target.files?.[0]) handleUploadDoc(type, e.target.files[0]); }}
                        data-testid={`upload-${type}`}
                      />
                      <span className={`px-3 py-1.5 rounded-md text-xs font-medium ${uploading === type ? 'bg-[#333] text-[#666]' : 'bg-[#C9A24D] text-black cursor-pointer hover:bg-[#d4af5a]'}`}>
                        {uploading === type ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 inline mr-1" />Choisir</>}
                      </span>
                    </label>
                  )}
                </div>
              ))}
              <Button onClick={handleFinish} className="w-full h-11 bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold" data-testid="finish-registration">
                {docs.kbis && docs.rc_pro ? 'Terminer' : 'Passer cette étape'}
              </Button>
              <p className="text-[#555] text-xs text-center">Vous pourrez ajouter vos documents ultérieurement</p>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="bg-[#141414] border-[#2A2A2A]">
            <CardContent className="p-8 text-center" data-testid="partner-success">
              <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Demande envoyée</h2>
              <p className="text-[#A0A0A0] text-sm mb-4">Votre demande d'inscription a été soumise avec succès. Notre équipe l'examinera dans les plus brefs délais.</p>
              <p className="text-[#C9A24D] text-xs mb-6">Vous recevrez un email de confirmation une fois votre compte activé.</p>
              <Link to="/">
                <Button className="bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold px-8" data-testid="back-to-home">Retour à l'accueil</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
